import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { validatePrivacyLevel } from '~/_shared/components/mapPrivacySelector/mapPrivacySelector.helpers';
import { PasswordOption } from '~/_shared/components/mapPrivacySelector/mapPrivacySelector.types';
import { useMapPrivacyRestrictions } from '~/_shared/components/mapPrivacySelector/useMapPrivacyRestrictions.hook';
import { Page } from '~/_shared/constants/routes';
import {
  MapPrivacyLevel, type MapSource,
} from '~/_shared/types/map';
import { copy } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ShareMapEmailResult, ShareMapEmailResults,
} from '~/map/map.repository';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { mapInfoUpdatePrivacy } from '~/store/mapInfo/mapInfo.actionCreators';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useGetCurrentPageInfo } from '~/topBar/navigation/navigation.helpers';
import { type ShareMethod } from './sections/shareSection.component';
import { StartingView } from './sections/startingMapViewSection.component';
import { useShareMapSendEmail } from './sendEmail/useShareMapSendEmail';
import {
  sectionToFieldMapping, type ShareInputField,
} from './shareInputField.enum';
import {
  ShareMapComponent, ShareMapSection,
} from './shareMap.component';
import {
  type FieldErrors, useShareMap,
} from './useShareMap';

const sectionsOrder: ReadonlyArray<ShareMapSection> = [
  ShareMapSection.PrivacyLevel,
  ShareMapSection.StartingMap,
  ShareMapSection.Share,
];

const moveToSection = (expandedSections: ReadonlySet<ShareMapSection>, targetSection: ShareMapSection): ReadonlySet<ShareMapSection> => {
  const index = sectionsOrder.indexOf(targetSection);
  if (index < 1) {
    return expandedSections;
  }

  const target = sectionsOrder[index];
  const beforeTarget = sectionsOrder[index - 1];
  if (target && beforeTarget) {
    return copy.andAdd(copy.andRemove(expandedSections, [beforeTarget]), [target]);
  }

  return expandedSections;
};

const hasErrorOnPath = (targetSection: ShareMapSection, fieldErrors: FieldErrors): boolean => {
  const index = sectionsOrder.indexOf(targetSection);
  if (index < 0) {
    return false;
  }

  return sectionsOrder.slice(0, index).some(section => getSectionErrors(section, fieldErrors).length > 0);
};

const getSectionErrors = (targetSection: ShareMapSection, fieldErrors: FieldErrors) => {
  const field = sectionToFieldMapping.get(targetSection);
  if (!field) {
    return [];
  }

  return fieldErrors.get(field) ?? [];
};

const getIsStartingViewSectionValid = (selected: StartingView | null, mapName: string) =>
  selected !== null && (selected !== StartingView.CurrentMapView || mapName.length > 0);

type SubmitShareMapRequestData = Readonly<{
  clientId: number | null;
  mapId: number | null;
  privacyLevel: MapPrivacyLevel | null;
  startingMap: StartingView | null;
  mapName: string;
  password: string | null | undefined;
}>;

export type ShareMapProps = {
  initialShareId?: string;
  isSnapshot: boolean;
  mapId: number;
  mapName: string;
  mapPrivacyLevel?: MapPrivacyLevel | null;
  mapSource: MapSource;
  parentMapPrivacy: MapPrivacyLevel | null; // parameter is required, so any dev is aware that they NEED to provide it
  onSuccess?: (newPrivacyLevel: MapPrivacyLevel) => void;
};

export type ShareMapContainerProps = ModalProps & ShareMapProps;

export const ShareMapContainer: FC<ShareMapContainerProps> = (props) => {
  const { onSuccess } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const isCalledFromMap = useGetCurrentPageInfo().page === Page.ADMIN_MAP;
  const mapInfo = useMapInfoDataSelector();
  const isCurrentMap = mapInfo?.id === props.mapId;

  const [privacyLevel, setPrivacyLevel] = useState<MapPrivacyLevel | null>(null);
  const [password, setPassword] = useState('');
  const [keepExistingPassword, setKeepExistingPassword] = useState(false);
  const [startingMap, setStartingMap] = useState<StartingView | null>(null);
  const [shareMethod, setSharingMethod] = useState<ShareMethod | null>(null);
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<ShareMapSection>>(new Set([ShareMapSection.PrivacyLevel]));
  const [mapName, setMapName] = useState('');
  const { shareId, isLoading, globalError, fieldErrors, shareMap, clearFieldError, clearGlobalError } = useShareMap();
  const [sendEmailResult, setSendEmailResult] = useState<ShareMapEmailResult | null>(null);

  const { isSendingEmail, sendShareMapEmail } = useShareMapSendEmail(props.mapId);
  const { openModal: openEmailNotificationModal } = useModal('EmailNotification');
  const mapPrivacyRestrictions = useMapPrivacyRestrictions({
    currentMapPrivacyLevel: privacyLevel || undefined,
    isSnapshot: props.isSnapshot,
    mapSource: props.mapSource,
    parentMapPrivacyLevel: props.parentMapPrivacy ?? undefined,
  });

  const submitShareMap = useCallback((data: SubmitShareMapRequestData) => {
    if (!data.privacyLevel || !getIsStartingViewSectionValid(data.startingMap, data.mapName)
      || !validatePrivacyLevel(data.privacyLevel, data.password || '') || fieldErrors.size > 0
      || !mapPrivacyRestrictions.isValid
    ) {
      return;
    }

    const newPrivacyLevel = data.privacyLevel || MapPrivacyLevel.Public;

    shareMap({
      ...data,
      privacyLevel: newPrivacyLevel,
      password: data.password ?? null,
      useCurrentMapView: data.startingMap === StartingView.CurrentMapView,
    })
      .then(({ fieldErrors, globalError }) => {
        const sectionsToExpand = Array.from(sectionToFieldMapping.keys())
          .filter(section => fieldErrors.has(sectionToFieldMapping.get(section) as ShareInputField));
        setExpandedSections(prev => copy.andAdd(prev, sectionsToExpand));
        if (fieldErrors.size === 0 && globalError) {
          setExpandedSections(prev => copy.andAdd(prev, [ShareMapSection.PrivacyLevel]));
        }

        if (fieldErrors.size === 0 && !globalError) {
          if (isCurrentMap) {
            dispatch(mapInfoUpdatePrivacy(newPrivacyLevel));
          }
          if (onSuccess) {
            onSuccess(newPrivacyLevel);
          }
        }
      });
  }, [dispatch, fieldErrors.size, isCurrentMap, mapPrivacyRestrictions.isValid, shareMap, onSuccess]);

  const onPrivacyLevelChange = useCallback((privacyLevel: MapPrivacyLevel, password?: string) => {
    setPrivacyLevel(privacyLevel);
    setPassword(password || '');
    setKeepExistingPassword(false);

    if (validatePrivacyLevel(privacyLevel, password)) {
      setExpandedSections(moveToSection(expandedSections, ShareMapSection.StartingMap));
      submitShareMap({ clientId, mapId: props.mapId, privacyLevel, password, startingMap, mapName });
    }
  }, [clientId, expandedSections, mapName, props.mapId, startingMap, submitShareMap]);

  const handlePasswordOptionChange = useCallback((option: PasswordOption) => {
    if (option === PasswordOption.preserve) {
      setPrivacyLevel(MapPrivacyLevel.PasswordProtected);
      setKeepExistingPassword(true);
      setExpandedSections(moveToSection(expandedSections, ShareMapSection.StartingMap));
      onSuccess?.(MapPrivacyLevel.PasswordProtected);
    }
    else {
      setKeepExistingPassword(false);
    }
  }, [expandedSections, onSuccess]);

  const onStartMapChange = useCallback((value: StartingView) => {
    setStartingMap(value);
    if (value === StartingView.CurrentMapView) {
      return;
    }

    setExpandedSections(moveToSection(expandedSections, ShareMapSection.Share));
    submitShareMap({ clientId, mapId: props.mapId, privacyLevel, password, startingMap: value, mapName });
  }, [clientId, expandedSections, mapName, password, privacyLevel, props.mapId, submitShareMap]);

  const onMapNameFormSubmit = useCallback((mapName: string) => {
    setMapName(mapName);
    setExpandedSections(moveToSection(expandedSections, ShareMapSection.Share));
    submitShareMap({ clientId, mapId: props.mapId, privacyLevel, password, startingMap, mapName });
  }, [clientId, expandedSections, password, privacyLevel, props.mapId, startingMap, submitShareMap]);

  const onSubmitSendEmail = useCallback(async (emails: readonly string[], message: string) => {
    const result = await sendShareMapEmail(emails, message);

    if (result === ShareMapEmailResults.Success) {
      const onClose = props.onClose;
      onClose();
      openEmailNotificationModal({});
    }
    else {
      setSendEmailResult(result);
    }
  }, [props.onClose, sendShareMapEmail, openEmailNotificationModal]);

  const isPrivacyLevelValid = !!privacyLevel && (keepExistingPassword || validatePrivacyLevel(privacyLevel, password));
  const preservePasswordDisabledReason = startingMap === StartingView.CurrentMapView && expandedSections.has(ShareMapSection.PrivacyLevel)
    ? t('presentationalShare.password.createNewForMapView')
    : undefined;

  const lockedSections = useMemo(() => {
    if (!isPrivacyLevelValid) {
      if (preservePasswordDisabledReason) { // if it's disabled because of starting map, let user choose a different one
        return new Set([ShareMapSection.Share]);
      }
      return new Set([ShareMapSection.StartingMap, ShareMapSection.Share]);
    }
    const sections = new Set<ShareMapSection>();
    if (hasErrorOnPath(ShareMapSection.StartingMap, fieldErrors)) {
      sections.add(ShareMapSection.StartingMap);
    }
    if (!startingMap || hasErrorOnPath(ShareMapSection.Share, fieldErrors)) {
      sections.add(ShareMapSection.Share);
    }
    return sections;
  }, [fieldErrors, isPrivacyLevelValid, startingMap, preservePasswordDisabledReason]);

  useEffect(() => {
    if (!props.isOpen) {
      setPrivacyLevel(null);
      setExpandedSections(new Set([ShareMapSection.PrivacyLevel]));
      setPassword('');
      setStartingMap(null);
      setMapName('');
      setKeepExistingPassword(false);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (startingMap === StartingView.CurrentMapView && keepExistingPassword) {
      setKeepExistingPassword(false);
      setExpandedSections(prev => copy.andAdd(prev, [ShareMapSection.PrivacyLevel, ShareMapSection.StartingMap]));
    }
  }, [keepExistingPassword, startingMap]);

  const effectiveShareId = keepExistingPassword ? (props.initialShareId ?? '') : shareId;

  return (
    <ShareMapComponent
      isOpen={props.isOpen}
      isPasswordProtected={
        (isCurrentMap ? mapInfo?.privacy : props.mapPrivacyLevel) === MapPrivacyLevel.PasswordProtected
      }
      isCalledFromMap={isCalledFromMap}
      isSnapshot={props.isSnapshot}
      initialPasswordOption={preservePasswordDisabledReason ? PasswordOption.create : undefined}
      onClose={props.onClose}
      onChangeShareMethod={setSharingMethod}
      onChangeStartingMap={onStartMapChange}
      onMapFormSubmit={isPrivacyLevelValid ? onMapNameFormSubmit : undefined}
      onPrivacyLevelChange={onPrivacyLevelChange}
      onPasswordOptionChange={handlePasswordOptionChange}
      privacyLevel={privacyLevel}
      preservePasswordDisabledReason={preservePasswordDisabledReason}
      mapName={props.mapName}
      mapPrivacyRestrictions={mapPrivacyRestrictions}
      shareMethod={shareMethod}
      startingMap={startingMap}
      expandedSections={expandedSections}
      onToggleSection={section => setExpandedSections(copy.andTogglePresence(expandedSections, section))}
      lockedSections={lockedSections}
      isLoading={isLoading || isSendingEmail}
      globalError={globalError}
      onClearGlobalError={clearGlobalError}
      fieldErrors={fieldErrors}
      onClearFieldError={clearFieldError}
      shareId={effectiveShareId}
      onSubmitSendEmail={onSubmitSendEmail}
      sendEmailResult={sendEmailResult}
    />
  );
};
